import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { isMagicLinkError } from '@identity/application/errors';
import type { AccountRequirement } from '@identity/domain/types';
import {
  ActivationLayout,
  FullScreenLoader,
  isRetryableLinkErrorCode,
  LinkErrorScreen,
  nonLinkErrorCode,
} from '@identity/presentation/components/base';
import type {
  ActivationStepItem,
  LinkErrorScreenCode,
  LinkErrorScreenCopy,
} from '@identity/presentation/components/base';
import { PasswordStep, PhoneStep } from '@identity/presentation/components/specific/steps';
import { INVITATION_STEP_ORDER, SIGN_IN_ROUTE } from '@identity/presentation/constants';
import { useAccountStatus } from '@identity/presentation/hooks';
import { completedStepsFor, nextStepFor, unsupportedRequirements } from '@identity/presentation/utils';

import { ActivationDoneScreen } from './ActivationDoneScreen';

/**
 * The only requirement this wizard has no screen for. It is part of its order and always arrives
 * met, because the link verified it, so deriving it means the backend sent this flow a step that
 * belongs to the other one — and then the wizard must stop, not skip it.
 */
const UNRENDERABLE_STEP: AccountRequirement = 'EMAIL';

/** Which error screen to show when the status query fails. */
const resolveStatusErrorCode = (error: unknown): LinkErrorScreenCode =>
  isMagicLinkError(error) ? error.code : nonLinkErrorCode(error);

/**
 * Controls the invitation flow: it reads the account status, derives the current step and renders it.
 *
 * A component and not a hook, because the step is not state: it is derived from the requirement map
 * on every render. There is no context, no reducer and no route per step — an openable URL per step
 * would need guards and redirects, putting the ordering back into the UI.
 *
 * Everything that differs from the other wizard is passed from here already translated, so no step
 * ever learns which flow it belongs to.
 *
 * It stops instead of hiding: a requirement this build cannot render ends the flow, because
 * finishing with an incomplete account is the one result it must never produce.
 *
 * It logs nothing. Every call is already reported by its use case.
 */
export const AccountActivationWizard = () => {
  const { t } = useTranslation('identity');
  const history = useHistory();
  const { data, error, isLoading, refetch } = useAccountStatus();

  const requirements = data?.requirements;
  const currentStep = requirements ? nextStepFor(INVITATION_STEP_ORDER, requirements) : undefined;
  // Two ways to block: a key this build does not know, or one it knows but cannot render.
  const blockingRequirement = requirements
    ? unsupportedRequirements(INVITATION_STEP_ORDER, requirements)[0] ??
      (currentStep === UNRENDERABLE_STEP ? UNRENDERABLE_STEP : undefined)
    : undefined;

  const onSignIn = () => history.push(SIGN_IN_ROUTE);

  if (error) {
    const code = resolveStatusErrorCode(error);

    const expiredCopy: LinkErrorScreenCopy = {
      body: t('activation.linkExpired.body'),
      title: t('activation.linkExpired.title'),
    };

    return (
      <LinkErrorScreen
        code={code}
        copy={code === 'LINK_EXPIRED' ? expiredCopy : undefined}
        onRetry={isRetryableLinkErrorCode(code) ? () => refetch() : undefined}
        onSignIn={onSignIn}
      />
    );
  }

  if (isLoading || !data || !requirements || !currentStep) return <FullScreenLoader />;

  // Stop, do not skip: the flow must never finish by ignoring a requirement.
  if (blockingRequirement) {
    const unsupportedCopy: LinkErrorScreenCopy = {
      body: t('activation.error.body'),
      title: t('activation.error.title'),
    };

    return <LinkErrorScreen code="LINK_INVALID" copy={unsupportedCopy} onSignIn={onSignIn} />;
  }

  const completedSteps = completedStepsFor(INVITATION_STEP_ORDER, requirements);
  const steps: ActivationStepItem[] = INVITATION_STEP_ORDER.filter(
    (requirement) => requirements[requirement] !== undefined,
  ).map((requirement) => ({
    isCompleted: completedSteps.includes(requirement),
    label: t(`activation.steps.${requirement}`),
    requirement,
  }));

  const headline =
    currentStep === 'DONE'
      ? t('activation.doneHeadline', { firstName: data.account.firstName })
      : t('activation.headline');

  return (
    <ActivationLayout currentStep={currentStep} headline={headline} steps={steps}>
      {currentStep === 'PASSWORD' && (
        <PasswordStep
          emailField={{ email: data.account.email, isConfirmed: requirements.EMAIL === true }}
          stepLabel={t('password.stepLabel')}
        />
      )}
      {currentStep === 'PHONE' && <PhoneStep stepLabel={t('phone.stepLabel')} />}
      {currentStep === 'DONE' && <ActivationDoneScreen />}
    </ActivationLayout>
  );
};
